import { ThemeProvider } from "@/components/ThemeProvider"
import { Toaster } from "@/components/ui/toaster"
import { TooltipProvider } from "@/components/ui/tooltip"
import { QueryClientProvider } from "@tanstack/react-query"
import { lazy, Suspense } from "react"
import { HelmetProvider } from "react-helmet-async"
import { Route, Switch } from "wouter"
import { queryClient } from "./lib/queryClient"

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
      <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
)

// CRITICAL: Load Home page immediately (no lazy loading)
import Home from "@/pages/Home"

// PRIMARY routes - Load quickly (small chunks)
const Browse = lazy(() => import("@/pages/Browse"))
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"))
const Login = lazy(() => import("@/pages/Login"))
const Register = lazy(() => import("@/pages/Register"))
const Pricing = lazy(() => import("@/pages/Pricing"))

// SECONDARY routes - Can load later (authenticated users)
const Dashboard = lazy(() => import("@/pages/Dashboard"))
const PostProperty = lazy(() => import("@/pages/PostProperty"))
const EditProperty = lazy(() => import("@/pages/EditProperty"))
const Profile = lazy(() => import("@/pages/Profile"))
const Messages = lazy(() => import("@/pages/Messages"))
const AgentListings = lazy(() => import("@/pages/AgentListings"))

// TERTIARY routes - Rarely accessed (can be heavily chunked)
const Featured = lazy(() => import("@/pages/Featured"))
const Subscribe = lazy(() => import("@/pages/subscribe"))
const PasswordRecovery = lazy(() => import("@/pages/PasswordRecovery"))
const ResetPassword = lazy(() => import("@/pages/reset"))
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"))

// Property Type Pages
const HouseForSale = lazy(() => import("@/pages/houseforsale"))
const CondosForRent = lazy(() => import("@/pages/CondosforRent"))
const Apartments = lazy(() => import("@/pages/Apartments"))
const Townhouses = lazy(() => import("@/pages/Townhouses"))
const LotsForSale = lazy(() => import("@/pages/LotsforSale"))
const ManilaProperties = lazy(() => import("@/pages/ManilaProperties"))
const MakatiCondos = lazy(() => import("@/pages/MakatiCondos"))
const BGCApartments = lazy(() => import("@/pages/BGCApartments"))
const CebuProperties = lazy(() => import("@/pages/CebuProperties"))
const DavaoRealEstate = lazy(() => import("@/pages/DavaoRealEstate"))
const ForeclosedProperties = lazy(() => import("@/pages/ForeclosedProperties"))
const PresellingCondos = lazy(() => import("@/pages/PresellingCondos"))
const Commercial = lazy(() => import("@/pages/Commercial"))
const AffordableHousing = lazy(() => import("@/pages/AffordableHousing"))

// Thank you pages - Group together (rarely accessed)
const FeaturedThankYou7Days = lazy(
  () => import("@/pages/FeaturedThankyou7Days")
)
const FeaturedThankYou14Days = lazy(
  () => import("@/pages/FeaturedThankyou14Days")
)
const FeaturedThankYou30Days = lazy(
  () => import("@/pages/FeaturedThankyou30Days")
)
const FeaturedThankYouBasic = lazy(
  () => import("@/pages/FeaturedThankyouBasic")
)
const FeaturedThankYouStandard = lazy(
  () => import("@/pages/FeaturedThankyouStandard")
)
const FeaturedThankYouPro = lazy(() => import("@/pages/FeaturedThankyouPro"))
const FeaturedThankYouBusiness = lazy(
  () => import("@/pages/FeaturedThankyouBusiness")
)
const FeaturedThankYouAdvanced = lazy(
  () => import("@/pages/FeaturedThankyouAdvanced")
)
const FeaturedThankYouEnterprise = lazy(
  () => import("@/pages/FeaturedThankyouEnterprise")
)

// 404 - Load last
const NotFound = lazy(() => import("@/pages/not-found"))

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* Home page - NO lazy loading for instant first paint */}
        <Route path="/" component={Home} />

        {/* Primary routes - Quick access */}
        <Route path="/browse" component={Browse} />
        <Route path="/pricing" component={Pricing} />

        {/* 🆕 SEO-friendly property routes - MUST come BEFORE /property/:id */}
        <Route path="/for-sale/:city/:slug" component={PropertyDetail} />
        <Route path="/for-rent/:city/:slug" component={PropertyDetail} />
        <Route path="/for-sale/:slug" component={PropertyDetail} />
        <Route path="/for-rent/:slug" component={PropertyDetail} />

        {/* Legacy property route - Keep for backward compatibility */}
        <Route path="/property/:id" component={PropertyDetail} />

        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />

        {/* Secondary routes - Authenticated users */}
        <Route path="/post-property" component={PostProperty} />
        <Route path="/edit-property/:id" component={EditProperty} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/profile" component={Profile} />
        <Route path="/messages" component={Messages} />

        {/* 🆕 UPDATED: Agent route now uses agentName instead of userId */}
        <Route path="/agent/:agentName" component={AgentListings} />

        {/* Property Type Routes */}
        <Route path="/houses-for-sale" component={HouseForSale} />
        <Route path="/condos-for-rent" component={CondosForRent} />
        <Route path="/apartments" component={Apartments} />
        <Route path="/townhouses" component={Townhouses} />
        <Route path="/lots-for-sale" component={LotsForSale} />
        <Route path="/manila-properties" component={ManilaProperties} />
        <Route path="/makati-condos" component={MakatiCondos} />
        <Route path="/bgc-apartments" component={BGCApartments} />
        <Route path="/cebu-properties" component={CebuProperties} />
        <Route path="/davao-real-estate" component={DavaoRealEstate} />
        <Route path="/foreclosed-properties" component={ForeclosedProperties} />
        <Route path="/preselling-condos" component={PresellingCondos} />
        <Route path="/commercial" component={Commercial} />
        <Route path="/affordable-housing" component={AffordableHousing} />

        {/* Tertiary routes - Less frequent */}
        <Route path="/feature-property/:id" component={Featured} />
        <Route path="/subscribe" component={Subscribe} />
        <Route path="/password-recovery" component={PasswordRecovery} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/admin" component={AdminDashboard} />

        {/* Featured Thank You Pages */}
        <Route
          path="/featured-thank-you-7days"
          component={FeaturedThankYou7Days}
        />
        <Route
          path="/featured-thank-you-14days"
          component={FeaturedThankYou14Days}
        />
        <Route
          path="/featured-thank-you-30days"
          component={FeaturedThankYou30Days}
        />

        {/* Subscription Thank You Pages */}
        <Route
          path="/subscribe-thank-you-basic"
          component={FeaturedThankYouBasic}
        />
        <Route
          path="/subscribe-thank-you-standard"
          component={FeaturedThankYouStandard}
        />
        <Route
          path="/subscribe-thank-you-pro"
          component={FeaturedThankYouPro}
        />
        <Route
          path="/subscribe-thank-you-business"
          component={FeaturedThankYouBusiness}
        />
        <Route
          path="/subscribe-thank-you-advanced"
          component={FeaturedThankYouAdvanced}
        />
        <Route
          path="/subscribe-thank-you-enterprise"
          component={FeaturedThankYouEnterprise}
        />

        {/* 404 Not Found - Must be last */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  )
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
